import type { ReactNode } from 'react';
import { Logo } from './Logo';
import { Icon, type IconName } from './Icon';

export interface BrandFeature {
  icon: IconName;
  title: string;
  body: string;
}

interface BrandPanelProps {
  /** Eyebrow tagline or short headline displayed above the body. */
  heading: ReactNode;
  /** Supporting paragraph. */
  body: ReactNode;
  /** Optional feature list (icon + title + sub-text). */
  features?: BrandFeature[];
  /** Optional bottom-row text. */
  footer?: ReactNode;
}

/**
 * Left-side panel used on the desktop login + register layouts. Hidden on
 * mobile via Tailwind (`hidden md:flex` on the wrapper that includes it).
 */
export function BrandPanel({ heading, body, features, footer }: BrandPanelProps) {
  return (
    <aside className="flex w-[480px] flex-col border-r border-line bg-paper-2 px-9 py-8">
      <Logo size={22} />
      <div className="flex flex-1 flex-col justify-center gap-5">
        <div className="text-[26px] font-semibold leading-[1.18] tracking-[-0.025em]">
          {heading}
        </div>
        <div className="max-w-[380px] text-sm leading-[1.55] text-muted">{body}</div>
        {features && (
          <div className="mt-1 flex flex-col gap-2.5">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line bg-paper text-ink-2">
                  <Icon name={f.icon} size={14} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold tracking-[-0.01em]">{f.title}</div>
                  <div className="text-[12px] text-muted">{f.body}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {footer && <div className="text-[11.5px] text-muted">{footer}</div>}
    </aside>
  );
}
